import * as React from 'react';
import {
  Button,
  Loader,
  Panel,
  PanelHeader,
  PanelBody,
  PanelFooter,
  PanelRow,
  PanelCell,
  Grid,
  Cell,
  Icon,
} from '@teamsnap/teamsnap-ui';
import Modal from '../../components/common/modal';
import { Role } from 'auth';
import { useFieldArray, useForm } from 'react-hook-form';
import { QUERY as QueryProgramsForAdmin } from '../../core/gql/queries/programs-for-admin';
import { useMutation, useQuery } from '@apollo/client';
import { GqlOrganization } from 'core/gql/fragments/organization';
import { reactiveFeedbackMessage } from 'core/apollo-cache';
import * as CreateTsSupportAdminRole from '../../core/gql/mutations/create-ts-support-admin-role';
import * as DeleteTsSupportAdminRole from '../../core/gql/mutations/delete-ts-support-admin-role';
import * as ShowUser from 'core/gql/queries/show-user';
import { APP_URL } from 'core/constants';

const filterAndMapTsSupportRoles = (roles, orgId) => {
  const mappedRoles = [];
  roles
    ?.filter((role) => role.organizationId == orgId && role.isTsSupportAdmin)
    .map((role) =>
      mappedRoles.push({
        type: role.type,
        scope: `${role.scopeType}:${role.scopeId}`,
        isNew: false,
      })
    );
  mappedRoles.push({ type: '', scope: '', isNew: true });
  return mappedRoles;
};

export interface Props {
  shouldShowModal: boolean;
  setShouldShowModal: (shouldShowModal) => any;
  selectedOrganization: GqlOrganization;
}

const OrganizationAddRoles: React.FunctionComponent<Props> = ({
  selectedOrganization,
  shouldShowModal,
  setShouldShowModal,
}) => {
  const addRolesForm = useForm({});
  const [numOfPrevRoles, setNumPrevRoles] = React.useState(0);
  const [isSubmitting, seIsSubmitting] = React.useState(false);
  const { handleSubmit, register, watch, control, reset } = addRolesForm;
  const programsQuery = useQuery(QueryProgramsForAdmin, { variables: { organizationId: +selectedOrganization.id } });
  const showUser = useQuery(ShowUser.QUERY);
  const [createTsSupportAdminRole] = useMutation(CreateTsSupportAdminRole.MUTATION, {
    refetchQueries: [ShowUser.QUERY],
  });
  const [deleteTsSupportAdminRole] = useMutation(DeleteTsSupportAdminRole.MUTATION, {
    refetchQueries: [ShowUser.QUERY],
  });

  const { fields, append } = useFieldArray({
    control,
    name: 'roles',
  });

  React.useEffect(() => {
    if (programsQuery.data && showUser.data?.user?.roles) {
      const loadedRoles = filterAndMapTsSupportRoles(showUser.data?.user?.roles, selectedOrganization.id);
      setNumPrevRoles(loadedRoles.filter((role) => !role.isNew).length);
      append(loadedRoles);
    }
  }, [programsQuery.data, showUser.data]);

  const submit = async (values) => {
    seIsSubmitting(true);
    const newRoles = values.roles.slice(numOfPrevRoles);
    const formattedRoles = newRoles.map((field: { scope: string; type: any }) => ({
      scopeType: field.scope.split(':')[0],
      scopeId: field.scope.split(':')[1],
      type: field.type,
    }));

    try {
      await createTsSupportAdminRole({
        variables: {
          organizationId: +selectedOrganization.id,
          supportRoles: formattedRoles,
        },
      });
      reactiveFeedbackMessage({
        message: 'Support Admin Roles Created',
        isError: false,
        autoclose: true,
      });
      reset();
      window.open(`${APP_URL}/organizations/${selectedOrganization.id}/programs`, '_blank');
      seIsSubmitting(false);
    } catch (e) {
      reactiveFeedbackMessage({
        message: 'Oops! We dropped the ball. Please try to saving again.',
        isError: true,
        autoclose: true,
      });
    }
  };

  const removeTsSupportRoles = async () => {
    try {
      await deleteTsSupportAdminRole({
        variables: {
          organizationId: +selectedOrganization.id,
        },
      });
      reset();
      reactiveFeedbackMessage({
        message: 'Support Admin Roles Deleted',
        isError: false,
        autoclose: true,
      });
    } catch (e) {
      reactiveFeedbackMessage({
        message: 'Oops! We dropped the ball. Please try to deleting again.',
        isError: true,
        autoclose: true,
      });
    }
  };

  return (
    <Modal isOpen={shouldShowModal} onClose={() => setShouldShowModal(false)}>
      <div id="organization-add-roles-content" className="page-content">
        <form onSubmit={handleSubmit(submit)}>
          <Panel mods="Panel--form">
            <PanelHeader title={`Add Myself to ${selectedOrganization.name}`} />
            <PanelBody>
              <PanelRow isWithCells={true} mods="Panel-row--header">
                <PanelCell mods="Panel-cell--header u-size3of12 u-flex">
                  <h4>Roles</h4>
                </PanelCell>
                <PanelCell mods="u-size9of12">
                  <Grid isWithGutter={true}>
                    <Cell mods="u-size12of12">
                      <div>What role(s) should you be assigned when added to this organization?</div>
                    </Cell>
                  </Grid>
                  <div className="u-spaceTopLg">
                    {fields.map(({ id, type, scope, isNew }, index) => {
                      const role = watch(`roles[${index}]`);

                      const hasError =
                        addRolesForm.errors['roles']?.[index]?.['type'] ||
                        addRolesForm.errors['roles']?.[index]?.['scope'];

                      return (
                        <Grid key={id} isWithGutter mods="u-spaceTopSm u-posRelative">
                          <Cell mods="u-size6of12">
                            <div
                              className={`FieldGroup ${addRolesForm.errors['roles']?.[index]?.['type'] ? 'error' : ''}`}
                            >
                              <div className="SelectBox">
                                <select
                                  ref={register({
                                    required: true,
                                  })}
                                  className={'SelectBox-options'}
                                  name={`roles[${index}].type`}
                                  data-testid={`roles[${index}].type`}
                                  defaultValue={type}
                                  disabled={!isNew}
                                >
                                  <option value="">Select Role</option>
                                  <option value={Role.OrgAdmin}>Organization Admin</option>
                                  <option value={Role.ProgramAdmin}>Program Admin</option>
                                  <option value={Role.ProgramCoordinator}>Program Coordinator</option>
                                </select>
                              </div>
                            </div>
                          </Cell>
                          {[Role.ProgramAdmin, Role.ProgramCoordinator].includes(role?.type) && (
                            <Cell mods="u-size6of12">
                              <div
                                className={`FieldGroup ${
                                  addRolesForm.errors['roles']?.[index]?.['scope'] ? 'error' : ''
                                }`}
                              >
                                <div className="SelectBox">
                                  <select
                                    ref={register({
                                      required: true,
                                    })}
                                    className={'SelectBox-options '}
                                    name={`roles[${index}].scope`}
                                    data-testid={`roles[${index}].scope`}
                                    defaultValue={scope}
                                    disabled={!isNew}
                                  >
                                    <option value="">Select a Program</option>
                                    {programsQuery.data?.programsForAdmin
                                      ?.slice()
                                      .sort((a, b) => a.name.localeCompare(b.name))
                                      .map((program) => (
                                        <option key={`program:${program.id}`} value={`program:${program.id}`}>
                                          {program.name}
                                        </option>
                                      ))}
                                  </select>
                                </div>
                              </div>
                            </Cell>
                          )}
                          {[Role.OrgAdmin].includes(role?.type) && (
                            <Cell mods="u-size6of12">
                              <div className={'FieldGroup'}>
                                <div className="SelectBox">
                                  <div className="SelectBox">
                                    <select
                                      ref={register({
                                        required: true,
                                      })}
                                      disabled
                                      className={'SelectBox-options'}
                                      name={`roles[${index}].scope`}
                                      data-testid={`roles[${index}].scope`}
                                      defaultValue={scope}
                                    >
                                      <option value={`organization:${selectedOrganization.id}`}>
                                        Entire Organization
                                      </option>
                                    </select>
                                  </div>
                                </div>
                              </div>
                            </Cell>
                          )}
                          {!role?.type && (
                            <Cell mods="u-size6of12">
                              <div className={`FieldGroup ${hasError ? 'error' : ''}`}>
                                <div className="SelectBox">
                                  <div className="SelectBox">
                                    <select
                                      ref={register({
                                        required: true,
                                      })}
                                      disabled
                                      className={'SelectBox-options'}
                                      name={`roles[${index}].scope`}
                                      data-testid={`roles[${index}].scope`}
                                      defaultValue={scope}
                                    >
                                      <option value={`organization:${selectedOrganization.id}`}>Select Program</option>
                                    </select>
                                  </div>
                                </div>
                              </div>
                            </Cell>
                          )}
                          {hasError && <Cell mods="validation-error">You must assign a role.</Cell>}
                        </Grid>
                      );
                    })}
                    <Grid isWithGutter mods="u-spaceTopSm u-posRelative">
                      <Cell mods="u-size6of6">
                        <div className="u-flex u-flexJustifyBetween u-flexAlignItemsCenter">
                          <div>
                            {numOfPrevRoles > 0 && (
                              <Button type="link" onClick={() => removeTsSupportRoles()} testId="btn-remove-roles">
                                <Icon mods="u-spaceRightXs" name="trash" />
                                Remove Roles
                              </Button>
                            )}
                          </div>
                          <Button
                            type="link"
                            onClick={() => append({ type: '', scope: '', isNew: true })}
                            mods="u-spaceTopSm u-textRight u-colorPrimary6"
                            testId="btn-add-role-row"
                          >
                            + Add Another
                          </Button>
                        </div>
                      </Cell>
                    </Grid>
                  </div>
                </PanelCell>
              </PanelRow>
            </PanelBody>
            <PanelFooter mods="u-textLeft">
              <div className="button-list">
                <Button mods="Button--negative" onClick={() => setShouldShowModal(false)}>
                  Cancel
                </Button>
                <Button
                  testId="btn-add-role"
                  isDisabled={isSubmitting}
                  color="primary"
                  icon={isSubmitting ? null : 'check'}
                  type="submit"
                >
                  {isSubmitting && <Loader type="spin" style={{ width: '13px', height: '13px' }} />}
                  Add Roles
                </Button>
              </div>
            </PanelFooter>
          </Panel>
        </form>
      </div>
    </Modal>
  );
};

export default OrganizationAddRoles;
